import React from 'react';
import { styled as styled2 } from '@compiled/react';
import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import JiraStatusLozenge from '@atlassian/jira-common-components-status-lozenge/src/view.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import Priority from './priority/index.tsx';
import IssueCardStatusLozenge from './status-lozenge/index.tsx';
import type { Props } from './types.tsx';

const IssueCard = ({
	issueKey,
	issueSummary,
	issueTypeIconUrl,
	statusCategoryId,
	statusName,
	issuePriorityUrl,
	issuePriorityName,
}: Props): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const route = useCurrentRoute();

	const StatusLozenge = fg('assets_app_migration_jira_dependency_migration_1')
		? IssueCardStatusLozenge
		: JiraStatusLozenge;

	const statusLozenge = statusCategoryId !== undefined && statusName !== undefined && (
		<StatusLozenge category={statusCategoryForId(statusCategoryId)} name={statusName} />
	);
	const priority = issuePriorityName !== undefined && issuePriorityUrl !== undefined && (
		<IssuePriority>
			<Priority iconUrl={issuePriorityUrl} name={issuePriorityName} />
		</IssuePriority>
	);

	return (
		<Container>
			<Tooltip content={issueSummary}>
				<IssueContainer
					href={`/browse/${issueKey}`}
					target="_blank"
					onClick={() => {
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'link',
							}),
							'incidentLink',
							{
								routeGroup: route.group ?? undefined,
								routeName: route.name ?? undefined,
							},
						);
					}}
				>
					<IssueTypeIcon src={issueTypeIconUrl} alt="" role="presentation" />
					<IssueKey>{issueKey}</IssueKey>
					<IssueSummary>{issueSummary}</IssueSummary>
					{priority}
					{statusLozenge}
				</IssueContainer>
			</Tooltip>
		</Container>
	);
};

export default IssueCard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssuePriority = styled2.span({
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginRight: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueTypeIcon = styled2.img({
	height: '16px',
	width: '16px',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: 0,

	borderRadius: token('radius.xsmall'),
	flex: '0 0 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueContainer = styled2.a({
	display: 'inline-flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
	paddingTop: 0,
	paddingRight: token('space.050'),
	paddingBottom: 0,
	paddingLeft: token('space.050'),

	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	backgroundColor: token('elevation.surface.raised'),
	cursor: 'pointer',
	boxShadow: token('elevation.shadow.raised'),
	'&:focus, &:hover, &:active': {
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'& > :last-child': {
			textDecoration: 'underline',
		},
	},
	maxWidth: '100%',
	boxSizing: 'border-box',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueSummary = styled2.span({
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	flex: '0 1 auto',
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginRight: token('space.050'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueKey = styled2.span({
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.semibold'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '24px',

	color: token('color.text.subtle'),
	marginTop: 0,
	marginRight: token('space.100'),
	marginBottom: 0,
	marginLeft: 0,
	flex: '0 0 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Container = styled2.div({
	marginTop: 0,
	marginRight: token('space.100'),
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginTop: 0,
		marginRight: token('space.100'),
		marginBottom: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginLeft: '1px',
	},
});
